import { Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { OsLotReviewDetailHeader } from '../../components/os-lot-reviews/os-lot-review-detail-header';
import { OsLotReviewLinkCard } from '../../components/os-lot-reviews/os-lot-review-link-card';
import { osLotReviewsApi } from '../../lib/apis/os-lot-reviews';
import { osLotReviewScopeFromSearch } from '../../lib/os-lot-review-scope';
import { useOsLotReviewsQueries } from '../../lib/queries/os-lot-reviews';
import { OsLotReviewSubmitPayload } from '../../lib/types/os-lot-reviews';
import { ErrorPage } from '../os-lot-reviews/page.error';

import { FullMainHeight } from '@ui';

export function OsLotReviewDetailPage() {
  const { t } = useTranslation();
  const { lotId } = useParams({ from: '/_app/os/lot-reviews_/$lotId' });
  const search = useSearch({ from: '/_app/os/lot-reviews_/$lotId' });
  const scope = osLotReviewScopeFromSearch(search);
  const numericId = Number(lotId);
  const queries = useOsLotReviewsQueries();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    ...queries.detail(numericId, scope),
  });

  const submitMutation = useMutation({
    mutationFn: ({
      linkId,
      payload,
    }: {
      linkId: number;
      payload: OsLotReviewSubmitPayload;
    }) => osLotReviewsApi.submitLink(numericId, linkId, payload, scope),
    onSuccess: async () => {
      notifications.show({
        color: 'green',
        message: t('osLotReviews.notifications.submit_success'),
      });
      await queryClient.invalidateQueries({ queryKey: queries.all() });
    },
    onError: () => {
      notifications.show({
        color: 'red',
        message: t('osLotReviews.notifications.submit_error'),
      });
    },
  });

  const lot = detailQuery.data;

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <OsLotReviewDetailHeader lot={lot} scope={scope} />
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <Stack gap="md" p="md">
          {lot && lot.links.length === 0 ? (
            <Text c="dimmed">{t('osLotReviews.no_links')}</Text>
          ) : null}
          {lot?.links.map((link) => (
            <OsLotReviewLinkCard
              isSubmitting={
                submitMutation.isPending &&
                submitMutation.variables?.linkId === link.id
              }
              key={link.id}
              link={link}
              lot={lot}
              onSubmit={(payload) =>
                submitMutation.mutate({ linkId: link.id, payload })
              }
            />
          ))}
        </Stack>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

OsLotReviewDetailPage.Error = ErrorPage;

export default OsLotReviewDetailPage;
